import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface TreatmentDetail {
  id: string
  treatment_number: number
  patient_id: string
  veterinarian_id: string | null
  treatment_type: string
  diagnosis: string
  clinical_notes: string
  temperature: number | null
  heart_rate: number | null
  respiratory_rate: number | null
  weight_at_visit: number | null
  status: string
  treatment_date: string
  follow_up_date: string | null
  follow_up_notes: string
  parent_treatment_id: string | null
  created_at: string
  updated_at: string
  patient_name: string
  patient_species: string
  veterinarian_name: string | null
}

export interface Procedure {
  id: string
  treatment_id: string
  name: string
  description: string
  status: string
  created_at: string
}

export interface TreatmentPrescription {
  id: string
  prescription_number: number
  patient_id: string
  medication_id: string
  treatment_id: string
  dosage: string
  frequency: string
  duration: string
  status: string
  medication_name: string
  patient_name: string
}

interface TreatmentsState {
  treatment: TreatmentDetail | null
  procedures: Procedure[]
  prescriptions: TreatmentPrescription[]
  loading: boolean
  error: string | null
}

const initialState: TreatmentsState = {
  treatment: null,
  procedures: [],
  prescriptions: [],
  loading: false,
  error: null,
}

export const fetchTreatment = createAsyncThunk(
  'treatments/fetchOne',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/treatments/${id}`)
    if (!res.ok) throw new Error('Failed to fetch treatment')
    return res.json() as Promise<TreatmentDetail>
  }
)

export const updateTreatment = createAsyncThunk(
  'treatments/update',
  async ({ id, data }: { id: string; data: Partial<TreatmentDetail> }) => {
    const res = await fetch(`/.netlify/functions/treatments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update treatment')
    return res.json() as Promise<TreatmentDetail>
  }
)

export const createTreatment = createAsyncThunk(
  'treatments/create',
  async (data: Partial<TreatmentDetail>) => {
    const res = await fetch('/.netlify/functions/treatments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create treatment')
    return res.json() as Promise<TreatmentDetail>
  }
)

export const fetchProcedures = createAsyncThunk(
  'treatments/fetchProcedures',
  async (treatmentId: string) => {
    const res = await fetch(`/.netlify/functions/treatment-procedures?treatment_id=${treatmentId}`)
    if (!res.ok) throw new Error('Failed to fetch procedures')
    return res.json() as Promise<Procedure[]>
  }
)

export const addProcedure = createAsyncThunk(
  'treatments/addProcedure',
  async (data: { treatment_id: string; name: string; description: string }) => {
    const res = await fetch('/.netlify/functions/treatment-procedures', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add procedure')
    return res.json() as Promise<Procedure>
  }
)

export const updateProcedure = createAsyncThunk(
  'treatments/updateProcedure',
  async ({ id, data }: { id: string; data: Partial<Procedure> }) => {
    const res = await fetch(`/.netlify/functions/treatment-procedures/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update procedure')
    return res.json() as Promise<Procedure>
  }
)

export const fetchTreatmentPrescriptions = createAsyncThunk(
  'treatments/fetchPrescriptions',
  async (treatmentId: string) => {
    const res = await fetch(`/.netlify/functions/treatment-prescriptions?treatment_id=${treatmentId}`)
    if (!res.ok) throw new Error('Failed to fetch prescriptions')
    return res.json() as Promise<TreatmentPrescription[]>
  }
)

const treatmentsSlice = createSlice({
  name: 'treatments',
  initialState,
  reducers: {
    clearTreatment(state) {
      state.treatment = null
      state.procedures = []
      state.prescriptions = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTreatment.pending, (state) => {
        if (!state.treatment) state.loading = true
      })
      .addCase(fetchTreatment.fulfilled, (state, action) => {
        state.treatment = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchTreatment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(updateTreatment.fulfilled, (state, action) => {
        state.treatment = action.payload
      })
      .addCase(fetchProcedures.fulfilled, (state, action) => {
        state.procedures = action.payload
      })
      .addCase(addProcedure.fulfilled, (state, action) => {
        state.procedures.push(action.payload)
      })
      .addCase(updateProcedure.fulfilled, (state, action) => {
        const idx = state.procedures.findIndex((p) => p.id === action.payload.id)
        if (idx !== -1) state.procedures[idx] = action.payload
      })
      .addCase(fetchTreatmentPrescriptions.fulfilled, (state, action) => {
        state.prescriptions = action.payload
      })
  },
})

export const { clearTreatment } = treatmentsSlice.actions
export default treatmentsSlice.reducer
